import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { useLang } from '@/hooks/useLang';

export default function NewsletterBar() {
  const { t } = useLang();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) { setStatus('error'); return; }
    /* TODO: Replace with Mailchimp action URL:
       fetch('https://xxx.us1.list-manage.com/subscribe/post?u=xxx&id=xxx', {
         method: 'POST', body: new FormData(e.target as HTMLFormElement)
       }) */
    setStatus('success');
    setEmail('');
    setTimeout(() => setStatus('idle'), 5000);
  };

  return (
    <section className="relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #0a0f20 0%, #0d1530 50%, #0a0f20 100%)',
      borderTop: '1px solid rgba(74,184,255,0.12)',
      borderBottom: '1px solid rgba(74,184,255,0.06)',
    }}>
      {/* Glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute left-1/4 top-0 w-px h-full opacity-[0.06]"
          style={{ background: 'linear-gradient(to bottom, transparent, #4ab8ff, transparent)' }} />
        <div className="absolute right-1/4 top-0 w-px h-full opacity-[0.04]"
          style={{ background: 'linear-gradient(to bottom, transparent, #4ab8ff, transparent)' }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[600px] h-[80px] rounded-full opacity-[0.06]"
            style={{ background: 'radial-gradient(ellipse, #4ab8ff, transparent)', filter: 'blur(40px)' }} />
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-16 py-10 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-8">

          {/* Left: Icon + Text */}
          <div className="flex items-start gap-4 shrink-0 text-center lg:text-left">
            <div className="hidden sm:flex w-12 h-12 rounded-xl items-center justify-center shrink-0"
              style={{ background: 'linear-gradient(135deg, rgba(74,184,255,0.15), rgba(41,92,168,0.2))', border: '1px solid rgba(74,184,255,0.2)', boxShadow: '0 4px 20px rgba(74,184,255,0.1)' }}>
              <Mail size={20} style={{ color: '#4ab8ff' }} />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase font-semibold mb-1" style={{ color: '#4ab8ff' }}>
                {t('NEWSLETTER', 'NEWSLETTER')}
              </p>
              <h3 className="text-lg font-bold text-white leading-snug">
                {t('Rămâi la curent cu noutățile IT', 'Stay Up-to-Date with IT News')}
              </h3>
              <p className="text-xs text-white/40 mt-1 max-w-xs">
                {t(
                  'Ghiduri tehnice, oferte exclusive și studii de caz — direct în inbox.',
                  'Technical guides, exclusive offers and case studies — straight to your inbox.'
                )}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px self-stretch opacity-10" style={{ background: 'linear-gradient(to bottom, transparent, #4ab8ff, transparent)' }} />

          {/* Right: Form */}
          <div className="flex-1 w-full max-w-lg">
            {status === 'success' ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-3 px-5 py-4 rounded-xl"
                style={{ background: 'rgba(74,184,255,0.08)', border: '1px solid rgba(74,184,255,0.2)' }}>
                <CheckCircle size={18} style={{ color: '#4ab8ff', flexShrink: 0 }} />
                <div>
                  <p className="text-sm font-semibold text-white">
                    {t('Abonare reușită!', 'Successfully subscribed!')}
                  </p>
                  <p className="text-xs text-white/45">
                    {t('Vei primi confirmarea pe email în câteva minute.', 'You\'ll receive a confirmation email shortly.')}
                  </p>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none" />
                    <input
                      type="email"
                      value={email}
                      onChange={e => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
                      placeholder={t('adresa@ta.com', 'your@email.com')}
                      required
                      className="w-full pl-9 pr-4 py-3 rounded-xl text-sm text-white placeholder-white/25 outline-none transition-all"
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: status === 'error'
                          ? '1px solid rgba(239,68,68,0.5)'
                          : '1px solid rgba(255,255,255,0.1)',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
                      }}
                      onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(74,184,255,0.45)'; (e.target as HTMLInputElement).style.boxShadow = '0 0 0 3px rgba(74,184,255,0.08), inset 0 1px 0 rgba(255,255,255,0.04)'; }}
                      onBlur={e => { (e.target as HTMLInputElement).style.borderColor = status === 'error' ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'; (e.target as HTMLInputElement).style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.04)'; }}
                    />
                  </div>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="shrink-0 px-6 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 btn-gradient"
                  >
                    {t('Abonează-te', 'Subscribe')} <ArrowRight size={14} />
                  </motion.button>
                </div>

                {status === 'error' && (
                  <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 mt-2">
                    <AlertCircle size={12} className="text-red-400 shrink-0" />
                    <p className="text-xs text-red-400/80">
                      {t('Adresă de email invalidă. Verificați și reîncercați.', 'Invalid email address. Please check and try again.')}
                    </p>
                  </motion.div>
                )}

                <p className="text-[10px] text-white/18 mt-3 leading-relaxed">
                  {t(
                    'Prin abonare ești de acord cu Politica noastră de Confidențialitate. Poți anula abonamentul oricând printr-un singur click.',
                    'By subscribing you agree to our Privacy Policy. You can unsubscribe at any time with a single click.'
                  )}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
